import {AxiosInstance} from "axios";
import Consumer from "@/core/interfaces/consumers/Consumer";
import {
    MedicineCabinetCategoryRaw,
    MedicineCabinetCategory,
    MedicineCabinetCategoryAdvice, MedicineCabinetCategoryProduct
} from "@/core/interfaces/medicine-cabinet/MedicineCabinet";
import Product from "@/product/interfaces/Product";
import {PaginatedData} from "@/core/interfaces/api";

interface MedicineCabinetCategoryQueryParams {
    prescription?: boolean;
    parent?: boolean;
}

/**
 * The survey resource.
 *
 * @param {Object} client - The injected client.
 * @returns {Object} The public object.
 */
export default function (client: AxiosInstance) {
    return {
        /**
         * Read patient's medicine cabinet.
         *
         * @param {number} healthcenterId - Healthcenter id.
         * @param {number} medicineCabinetId - Medicine cabinet id.
         *
         * @return {Promise}
         */
        readMedicineCabinet(healthcenterId: number, medicineCabinetId: number) {
            return client.get(`healthcenters/${healthcenterId}/medicine-cabinets/${medicineCabinetId}`)
                .then(response => response.data.data);
        },

        /**
         * Read consumer's medicine cabinet statistics.
         *
         * @param {number} healthcenterId - Healthcenter id.
         *
         * @return {Promise}
         */
        consumersStatistics(healthcenterId: number): Promise<Consumer[]> {
            return client.get(`healthcenters/${healthcenterId}/medicine-cabinet-statistics/consumers`)
                .then(response => response.data.data);
        },

        /**
         * Read statistics of a medicine cabinet category.
         *
         * @param {number} healthcenterId - The healthcenter id.
         *
         * @return {Promise}
         */
        statistics(healthcenterId: number) {
            return client.get(`healthcenters/${healthcenterId}/medicine-cabinet-statistics`)
                .then(response => response.data.data);
        },

        /**
         * Fetch medicine cabinet categories.
         *
         * @param {MedicineCabinetCategoryQueryParams} params Params of the request.
         *
         * @return {Promise<MedicineCabinetCategoryRaw[]>}
         */
        indexCategories(params?: MedicineCabinetCategoryQueryParams): Promise<MedicineCabinetCategoryRaw[]> {
            return client.get("medicine-cabinet-categories", {params})
                .then(response => response.data.data);
        },

        /**
         * Fetch a medicine cabinet category.
         *
         * @param {number} id Medicine cabinet category id
         *
         * @return {Promise<MedicineCabinetCategory>}
         */
        readCategory(id: number): Promise<MedicineCabinetCategory> {
            return client.get(`medicine-cabinet-categories/${id}`)
                .then(response => response.data.data);
        },

        /**
         * Create medicine cabinet category advice.
         *
         * @param {number} id Medicine cabinet category id.
         * @param {string} advice Medicine cabinet category advice.
         *
         * @return {Promise<MedicineCabinetCategoryAdvice>}
         */
        createAdvice(id: number, advice: string): Promise<MedicineCabinetCategoryAdvice> {
            const data = {
                content: advice
            };

            return client.post(`medicine-cabinet-categories/${id}/advice`, {data})
                .then(response => response.data.data);
        },

        /**
         * Update medicine cabinet category advice.
         *
         * @param {number} id Medicine cabinet category id.
         * @param {string} advice Medicine cabinet category advice.
         *
         * @return {Promise<MedicineCabinetCategoryAdvice>}
         */
        updateAdvice(id: number, advice: string): Promise<MedicineCabinetCategoryAdvice> {
            const data = {
                content: advice
            };

            return client.patch(`medicine-cabinet-categories/${id}/advice`, {data})
                .then(response => response.data.data);
        },

        /**
         * Read a medicine cabinet category advice.
         *
         * @param {number} id - The medicine cabinet category id.
         *
         * @return {Promise<MedicineCabinetCategoryAdvice>}
         */
        readAdvice(id: number): Promise<MedicineCabinetCategoryAdvice> {
            return client.get(`medicine-cabinet-categories/${id}/advice`)
                .then(response => response.data.data);
        },
        /**
         * Read products attached to medicine cabinet category.
         *
         * @param {number} id - The medicine cabinet category id.
         *
         * @return {Promise<Product[]>}
         */
        indexProducts(id: number): Promise<Product[]> {
            return client.get(`medicine-cabinet-categories/${id}/recommended-products`, {params: {limit: 100}})
                .then(response => response.data.data);
        },
        /**
         * Read products of a medicine cabinet category.
         *
         * @param {number} id - The medicine cabinet category id.
         * @param {string} sort - The sort option, either stock or sale.
         * @param {number} page - The page number.
         *
         *  @return {Promise<PaginatedData<MedicineCabinetCategoryProduct[]>>}
         */
        products(id: number, sort: "stock" | "sale", page: number): Promise<PaginatedData<MedicineCabinetCategoryProduct[]>> {
            const params = {sort, page};
            return client.get(`medicine-cabinet-categories/${id}/products`, {params})
                .then(response => response.data);
        },

        /**
         * Add products to medicine cabinet category.
         *
         * @param {number} id Medicine cabinet category id.
         * @param {number[]} productIds Medicine cabinet category product ids.
         *
         * @return {Promise<any>}
         */
        syncProducts(id: number, productIds: number[]): Promise<any> {
            const data = {
                product_ids: productIds
            };

            return client.post(`medicine-cabinet-categories/${id}/recommended-products`, {data})
                .then(response => response.data.data);
        },

        /**
         * Reorder products in medicine cabinet category.
         *
         * @param {number} id Medicine cabinet category id.
         * @param {number[]} productIds Medicine cabinet category product ids (sorted in the desired order).
         *
         * @return {Promise<any>}
         */
        orderProducts (id: number, productIds: number[]): Promise<any> {
            const data = {
                product_ids: productIds
            };

            return client.patch(`medicine-cabinet-categories/${id}/recommended-products-order`, {data})
                .then(response => response.data.data);
        },

        /**
         * Delete product from medicine cabinet category.
         *
         * @param {number} id Medicine cabinet category id.
         * @param {number} productId Product id.
         *
         * @return {Promise<Product>}
         */
        deleteProduct (id: number, productId: number): Promise<Product> {
            return client.delete(`medicine-cabinet-categories/${id}/recommended-products/${productId}`)
                .then(response => response.data.data);
        }
    };
}
